import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountingView = ({ formData, setFormData, selectedPlants = [], readOnly = false }) => {
  const [plants, setPlants] = useState([]);
  const [valuationTypes, setValuationTypes] = useState([]);
  const [valuationCategories, setValuationCategories] = useState([]);
  const [valuationClasses, setValuationClasses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [valuationAssignments, setValuationAssignments] = useState([]);
  const [availableValuationTypes, setAvailableValuationTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const accountingData = formData.accounting || {
    plant_id: '',
    valuation_type_id: '',
    valuation_category_id: '',
    valuation_class_id: '',
    ml_active: false,
    material_price_determination: '',
    currency: '',
    price_control: '',
    standard_price: '',
    moving_avg_price: ''
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    // When plant is selected, filter valuation types based on assignments
    if (accountingData.plant_id) {
      filterValuationTypesForPlant(accountingData.plant_id);
    } else {
      setAvailableValuationTypes([]);
    }
  // eslint-disable-next-line
  }, [accountingData.plant_id, valuationAssignments, valuationTypes]);

  const loadMasterData = async () => {
    try {
      const [
        plantsRes,
        valuationTypesRes,
        valuationCategoriesRes,
        valuationClassesRes,
        currenciesRes,
        assignmentsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/material-management/plants`),
        axios.get(`${BACKEND_URL}/api/valuation-types`),
        axios.get(`${BACKEND_URL}/api/valuation-categories`),
        axios.get(`${BACKEND_URL}/api/valuation-classes`),
        axios.get(`${BACKEND_URL}/api/master-data/currencies`),
        axios.get(`${BACKEND_URL}/api/valuation-type-category-assignments`)
      ]);

      setPlants(plantsRes.data || []);
      setValuationTypes(valuationTypesRes.data || []);
      setValuationCategories(valuationCategoriesRes.data || []);
      setValuationClasses(valuationClassesRes.data || []);
      setCurrencies(currenciesRes.data || []);
      setValuationAssignments(assignmentsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load accounting master data');
    } finally {
      setLoading(false);
    }
  };

  const filterValuationTypesForPlant = (plantId) => {
    // Filter assignments for the selected plant
    const plantAssignments = valuationAssignments.filter(
      assignment => assignment.plant_id === plantId
    );

    if (plantAssignments.length === 0) {
      setAvailableValuationTypes([]);
      return;
    }

    // Get unique valuation type IDs from assignments
    const validValuationTypeIds = [...new Set(plantAssignments.map(a => a.valuation_type_id))];

    // Filter valuation types to only show those with assignments
    const filtered = valuationTypes.filter(vt => 
      validValuationTypeIds.includes(vt.valuation_type_id)
    );

    setAvailableValuationTypes(filtered);
  };

  const getValuationCategoryForType = (valuationTypeId, plantId) => {
    if (!valuationTypeId || !plantId) return null;
    
    const assignment = valuationAssignments.find(
      a => a.valuation_type_id === valuationTypeId && a.plant_id === plantId
    );
    
    return assignment?.valuation_category_id || null;
  };

  const handleFieldChange = (field, value) => {
    const updates = { [field]: value };

    // When valuation type changes, auto-populate valuation category
    if (field === 'valuation_type_id' && value && accountingData.plant_id) {
      const category = getValuationCategoryForType(value, accountingData.plant_id);
      if (category) {
        updates.valuation_category_id = category;
      }
    }

    // When price control changes, reset price fields
    if (field === 'price_control') {
      if (value === 'S') {
        updates.moving_avg_price = '';
      } else if (value === 'V') {
        updates.standard_price = '';
      }
    }

    setFormData(prev => ({
      ...prev,
      accounting: {
        ...prev.accounting,
        ...updates
      }
    }));
  };

  const getAvailablePlants = () => {
    if (selectedPlants.length === 0) {
      return plants;
    }
    return plants.filter(p => selectedPlants.includes(p.plant_id));
  };

  const validatePriceInput = (value, priceControl) => {
    if (!value) return true;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.error('Price must be a valid number');
      return false;
    }
    
    if (numValue < 0) {
      toast.error('Price must be a positive value');
      return false;
    }
    
    // Check decimal places (max 2)
    const decimalPlaces = (value.split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      toast.error('Price can have maximum 2 decimal places');
      return false;
    }
    
    return true;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
        <p className="text-sm text-gray-600">Loading accounting master data...</p>
      </div>
    );
  }

  const availablePlants = getAvailablePlants();
  const plantConsistencyWarning = selectedPlants.length > 0;
  const showValuationTypeWarning = accountingData.plant_id && availableValuationTypes.length === 0;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">Accounting Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure financial accounting and valuation parameters for this material
        </p>
      </div>

      {/* Plant Consistency Warning */}
      {plantConsistencyWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-orange-900">Plant Consistency Enforced</h4>
            <p className="text-sm text-orange-700 mt-1">
              Plant selection is restricted to plants already selected in other views ({selectedPlants.join(', ')}).
            </p>
          </div>
        </div>
      )}

      {/* Plant & Valuation Type Selection */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-orange-900 mb-3">Plant & Valuation Configuration *</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Plant *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.plant_id}
              onChange={(e) => {
                handleFieldChange('plant_id', e.target.value);
                // Reset valuation fields when plant changes
                handleFieldChange('valuation_type_id', '');
                handleFieldChange('valuation_category_id', '');
              }}
            >
              <option value="">Select Plant</option>
              {availablePlants.map(p => (
                <option key={p.plant_id} value={p.plant_id}>
                  {p.plant_id} - {p.plant_name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Valuation area for accounting</p>
            {availablePlants.length === 0 && selectedPlants.length > 0 && (
              <p className="text-xs text-red-600 mt-1">No valid plants available. Please check plant selection in other views.</p>
            )}
          </div>
          <div>
            <Label>Valuation Type *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.valuation_type_id}
              onChange={(e) => handleFieldChange('valuation_type_id', e.target.value)}
              disabled={!accountingData.plant_id}
            >
              <option value="">
                {accountingData.plant_id ? 'Select Valuation Type' : 'Select Plant First'}
              </option>
              {availableValuationTypes.map(vt => (
                <option key={vt.valuation_type_id} value={vt.valuation_type_id}>
                  {vt.valuation_type_id}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">For split valuation (if applicable)</p>
            {showValuationTypeWarning && (
              <p className="text-xs text-red-600 mt-1">
                No valuation types configured for this plant. Please configure Valuation Type to Category Assignments first.
              </p>
            )}
          </div>
        </div>
        
        {/* Auto-populated Valuation Category */}
        {accountingData.valuation_category_id && (
          <div className="mt-4 bg-white rounded-md p-3 border border-gray-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-xs font-medium text-gray-700">Valuation Category (Auto-populated)</p>
                <p className="text-sm font-semibold text-gray-900">{accountingData.valuation_category_id}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Valuation Class */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">G/L Account Assignment</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Valuation Class *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.valuation_class_id}
              onChange={(e) => handleFieldChange('valuation_class_id', e.target.value)}
            >
              <option value="">Select Valuation Class</option>
              {valuationClasses.map(vc => (
                <option key={vc.valuation_class_id} value={vc.valuation_class_id}>
                  {vc.valuation_class_id} - {vc.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Links material to G/L accounts via OBYC</p>
          </div>
          <div>
            <Label>Currency *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.currency}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
            >
              <option value="">Select Currency</option>
              {currencies.map(c => (
                <option key={c.currency_code} value={c.currency_code}>
                  {c.currency_code} - {c.currency_name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Currency for price valuation</p>
          </div>
        </div>
      </div>

      {/* Price Control & Valuation */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Price Control & Valuation</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Price Control *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.price_control}
              onChange={(e) => handleFieldChange('price_control', e.target.value)}
            >
              <option value="">Select Price Control</option>
              <option value="S">S - Standard Price</option>
              <option value="V">V - Moving Average Price</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Determines price calculation method</p>
          </div>
          
          {accountingData.price_control === 'S' && (
            <div>
              <Label>Standard Price *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={accountingData.standard_price}
                onChange={(e) => {
                  if (validatePriceInput(e.target.value, 'S')) {
                    handleFieldChange('standard_price', e.target.value);
                  }
                }}
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">Cost per price unit (max 2 decimals)</p>
            </div>
          )}
          
          {accountingData.price_control === 'V' && (
            <div>
              <Label>Moving Average Price *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={accountingData.moving_avg_price}
                onChange={(e) => {
                  if (validatePriceInput(e.target.value, 'V')) {
                    handleFieldChange('moving_avg_price', e.target.value);
                  }
                }}
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">Calculated cost (max 2 decimals)</p>
            </div>
          )}
        </div>
      </div>

      {/* Material Ledger & Additional Settings */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Additional Accounting Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ml_active"
                checked={accountingData.ml_active}
                onChange={(e) => handleFieldChange('ml_active', e.target.checked)}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <Label htmlFor="ml_active" className="cursor-pointer">
                Material Ledger Active (ML Act)
              </Label>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Enables Actual Costing and periodic unit price calculation
            </p>
          </div>
          <div>
            <Label>Material Price Determination</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={accountingData.material_price_determination}
              onChange={(e) => handleFieldChange('material_price_determination', e.target.value)}
            >
              <option value="">Select Method (Optional)</option>
              <option value="1">1 - Transaction based</option>
              <option value="2">2 - Single/multilevel</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Price determination method (optional)</p>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-900 mb-1">📋 Important Notes:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Fields marked with * are mandatory</li>
          <li>Valuation Type is only required if Split Valuation is active for the material</li>
          <li>Valuation Category is auto-populated based on Plant + Valuation Type assignment</li>
          <li>Standard Price is required when Price Control = S</li>
          <li>Moving Average Price is required when Price Control = V</li>
          <li>All prices must be positive values with maximum 2 decimal places</li>
        </ul>
      </div>
    </div>
  );
};

export default AccountingView;
